import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { mockGoodsReceiptService } from '@/services/mock/MockGoodsReceiptService';
import { DeliveryTrackingRecord, GrnMetricsSummary } from '@/types/domain/grn-management';
import { DataTable, ColumnDef } from '@/components/data-display/DataTable';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { formatDate } from '@/lib/utils';
import {
  Truck,
  RefreshCw,
  Search,
  Eye,
  Download,
  Calendar,
  CheckCircle2,
  ArrowRight,
  PackagePlus,
  FileCheck,
  Send,
  X,
  Loader2,
  BellRing,
} from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { GrnKpiCards } from '@/features/grn/components/GrnKpiCards';
import { SlideOverDrawer } from '@/components/ui/SlideOverDrawer';
import { PurchaseOrderDetailPage } from '@/features/purchase-orders/pages/PurchaseOrderDetailPage';

export const DeliveryTrackingPage: React.FC = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<GrnMetricsSummary | null>(null);
  const [records, setRecords] = useState<DeliveryTrackingRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Expedite Notice Modal State
  const [expediteTarget, setExpediteTarget] = useState<DeliveryTrackingRecord | null>(null);
  const [expediteReason, setExpediteReason] = useState('Critical plant requirement - expedite shipment dispatch');
  const [isSendingExpedite, setIsSendingExpedite] = useState(false);
  const [expediteSuccess, setExpediteSuccess] = useState(false);
  const [selectedPoForDrawer, setSelectedPoForDrawer] = useState<string | null>(null);

  const [filters, setFilters] = useState({
    search: '',
    deliveryStatus: 'ALL',
    plant: 'ALL',
    page: 1,
    pageSize: 25,
  });

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [m, delivRes] = await Promise.all([
        mockGoodsReceiptService.getGrnMetricsSummary(),
        mockGoodsReceiptService.getDeliveries(filters),
      ]);
      setMetrics(m);
      setRecords(delivRes.items);
      setTotal(delivRes.total);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (!expediteTarget) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = orig;
    };
  }, [expediteTarget]);

  const handleExportCsv = () => {
    if (records.length === 0) return;
    const headers = [
      'PO Number',
      'Supplier',
      'Plant',
      'Promised Delivery',
      'Last Receipt',
      'Ordered Qty',
      'Received Qty',
      'Accepted Qty',
      'Pending Qty',
      'Status',
      'Delayed Days',
    ];
    const rows = records.map((r) => [
      r.poNumber,
      `"${r.vendorName}"`,
      r.plantCode,
      r.promisedDeliveryDate,
      r.lastReceiptDate || 'N/A',
      r.totalOrderedQuantity,
      r.totalReceivedQuantity,
      r.totalAcceptedQuantity,
      r.totalPendingQuantity,
      r.deliveryStatus,
      r.delayDays,
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Delivery_Tracking_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSendExpediteNotice = async () => {
    if (!expediteTarget) return;
    try {
      setIsSendingExpedite(true);
      // Simulate expedite dispatch to vendor
      await new Promise((resolve) => setTimeout(resolve, 600));
      setExpediteSuccess(true);
      setTimeout(() => {
        setExpediteSuccess(false);
        setExpediteTarget(null);
      }, 1200);
    } finally {
      setIsSendingExpedite(false);
    }
  };

  const getStatusBadge = (status: DeliveryTrackingRecord['deliveryStatus'], isDelayed: boolean, delayDays: number) => {
    if (isDelayed) {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shadow-2xs font-poppins bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20 whitespace-nowrap">
          <span className="h-1.5 w-1.5 rounded-full bg-rose-500 shrink-0" />
          Overdue ({delayDays}d)
        </span>
      );
    }

    switch (status) {
      case 'DELIVERED':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shadow-2xs font-poppins bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 whitespace-nowrap">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
            Fully Delivered
          </span>
        );
      case 'PARTIALLY_DELIVERED':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shadow-2xs font-poppins bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20 whitespace-nowrap">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />
            Partially Delivered
          </span>
        );
      case 'DUE_TODAY':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shadow-2xs font-poppins bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 whitespace-nowrap">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
            Due Today
          </span>
        );
      case 'QUALITY_HOLD':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shadow-2xs font-poppins bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20 whitespace-nowrap">
            <span className="h-1.5 w-1.5 rounded-full bg-purple-500 shrink-0" />
            Quality Hold
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shadow-2xs font-poppins bg-muted text-foreground-secondary border-border whitespace-nowrap">
            <span className="h-1.5 w-1.5 rounded-full bg-foreground-muted shrink-0" />
            {status}
          </span>
        );
    }
  };

  const columns: ColumnDef<DeliveryTrackingRecord>[] = [
    {
      header: 'PO Number & Scope',
      accessorKey: 'poNumber',
      sortable: true,
      cell: (row) => (
        <div className="space-y-0.5 font-poppins">
          <span
            onClick={() => navigate(ROUTES.PURCHASE_ORDERS.DETAILS(row.poId))}
            className="font-poppins font-bold text-primary hover:underline cursor-pointer whitespace-nowrap text-sm block"
          >
            {row.poNumber}
          </span>
          <span className="text-xs text-foreground-secondary truncate max-w-[200px] block" title={row.materialSummaries}>
            {row.materialSummaries}
          </span>
        </div>
      ),
    },
    {
      header: 'Supplier & Plant',
      accessorKey: 'vendorName',
      sortable: true,
      cell: (row) => (
        <div className="flex flex-col min-w-0 max-w-[190px] xl:max-w-[220px] font-poppins">
          <span className="font-bold text-foreground text-sm truncate block" title={row.vendorName}>
            {row.vendorName}
          </span>
          <span className="text-xs text-foreground-muted font-mono truncate block">
            {row.plantCode} • {row.vendorCode}
          </span>
        </div>
      ),
    },
    {
      header: 'Promised Delivery',
      accessorKey: 'promisedDeliveryDate',
      sortable: true,
      cell: (row) => (
        <div className="space-y-0.5 font-poppins text-xs">
          <span className="font-semibold text-foreground flex items-center gap-1 whitespace-nowrap">
            <Calendar className="h-3.5 w-3.5 text-primary" /> {formatDate(row.promisedDeliveryDate)}
          </span>
          <span className="text-[10px] text-foreground-muted block whitespace-nowrap">
            {row.lastReceiptDate ? `Last receipt: ${formatDate(row.lastReceiptDate)}` : 'Awaiting 1st Dispatch'}
          </span>
        </div>
      ),
    },
    {
      header: 'Fulfillment Progress',
      cell: (row) => {
        const percent = Math.min(
          100,
          Math.round((row.totalAcceptedQuantity / (row.totalOrderedQuantity || 1)) * 100)
        );
        return (
          <div className="font-poppins space-y-1 min-w-[140px]">
            <div className="flex items-center justify-between text-xs font-mono font-bold">
              <span className="text-emerald-600 dark:text-emerald-400">
                {row.totalAcceptedQuantity} <span className="text-foreground-muted font-normal text-[10px]">rec'd</span>
              </span>
              <span className="text-foreground font-semibold">
                {row.totalOrderedQuantity} <span className="text-foreground-muted font-normal text-[10px]">ordered</span>
              </span>
            </div>
            {/* Progress bar */}
            <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full transition-all rounded-full ${
                  percent === 100
                    ? 'bg-emerald-500'
                    : percent > 0
                    ? 'bg-blue-500'
                    : 'bg-muted-foreground/30'
                }`}
                style={{ width: `${percent}%` }}
              />
            </div>
            {row.totalPendingQuantity > 0 ? (
              <span className="text-[10px] text-amber-600 dark:text-amber-400 font-bold block">
                {row.totalPendingQuantity} units remaining
              </span>
            ) : (
              <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold block">
                100% In-Full (OTIF)
              </span>
            )}
          </div>
        );
      },
    },
    {
      header: 'Delivery Status',
      accessorKey: 'deliveryStatus',
      cell: (row) => (
        <div className="flex items-center font-poppins">
          {getStatusBadge(row.deliveryStatus, row.isDelayed, row.delayDays)}
        </div>
      ),
    },
    {
      header: 'Actions',
      cell: (row) => (
        <div
          className="flex items-center gap-1.5 justify-start shrink-0 whitespace-nowrap font-poppins min-w-[120px]"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedPoForDrawer(row.poId)}
            title="View Purchase Order Dossier (Slide-over)"
            className="h-8 w-8 p-0 rounded-lg hover:bg-primary/15 hover:text-primary active:scale-90 transition-all duration-200 cursor-pointer text-foreground-secondary shrink-0 inline-flex items-center justify-center group shadow-2xs hover:shadow-xs"
          >
            <Eye className="h-4 w-4 transition-transform duration-200 group-hover:scale-115" />
          </Button>

          {row.totalPendingQuantity > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(ROUTES.GRN.NEW, { state: { poNumber: row.poNumber, poId: row.poId } })}
              title="Create Goods Receipt (GRN)"
              className="h-8 w-8 p-0 rounded-lg text-emerald-600 hover:text-emerald-700 hover:bg-emerald-500/15 cursor-pointer shrink-0 inline-flex items-center justify-center"
            >
              <PackagePlus className="h-4 w-4" />
            </Button>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(ROUTES.GRN.LIST)}
            title="View Associated GRNs"
            className="h-8 w-8 p-0 rounded-lg hover:bg-primary/10 cursor-pointer text-primary hover:text-primary-dark shrink-0 inline-flex items-center justify-center"
          >
            <FileCheck className="h-4 w-4" />
          </Button>

          {row.isDelayed && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpediteTarget(row)}
              title="Send Urgent Expedite Notice"
              className="h-8 w-8 p-0 rounded-lg text-amber-600 hover:text-amber-700 hover:bg-amber-500/15 cursor-pointer shrink-0 inline-flex items-center justify-center"
            >
              <BellRing className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 pb-12 font-poppins">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-2.5 font-poppins">
            <Truck className="h-7 w-7 text-primary" /> Inbound Delivery Tracking & Fulfillment Control Center
          </h1>
          <p className="text-xs text-foreground-secondary font-medium mt-1 font-poppins">
            Real-time supplier dispatch milestones, promised date overdue detection, OTIF delivery compliance, and dock receiving progress.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            className="rounded-xl font-bold font-poppins cursor-pointer"
          >
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCsv}
            className="rounded-xl font-bold font-poppins cursor-pointer border-border"
          >
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>

          <Button
            onClick={() => navigate(ROUTES.GRN.LIST)}
            className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-primary-foreground font-poppins cursor-pointer shadow-xs"
          >
            Goods Receipts (GRN) <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      {metrics && (
        <GrnKpiCards
          metrics={metrics}
          onSelectFilter={(filterType, val) => {
            if (filterType === 'status') {
              setFilters((prev) => ({ ...prev, deliveryStatus: val }));
            }
          }}
        />
      )}

      {/* Main Card with Squeezed Status Tabs & Search */}
      <Card className="border-border rounded-2xl shadow-xs overflow-hidden font-poppins">
        <CardHeader className="p-4 border-b border-border/60 bg-muted/20">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            {/* Squeezed Status Tabs */}
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                { key: 'ALL', label: 'All Deliveries' },
                { key: 'OVERDUE', label: 'Overdue / Delayed' },
                { key: 'DUE_TODAY', label: 'Due Today' },
                { key: 'PARTIALLY_DELIVERED', label: 'Partially Delivered' },
                { key: 'DELIVERED', label: 'Fully Delivered' },
                { key: 'QUALITY_HOLD', label: 'Quality Hold' },
              ].map((tab) => {
                const isActive = filters.deliveryStatus === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setFilters((prev) => ({ ...prev, deliveryStatus: tab.key }))}
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer font-poppins ${
                      isActive
                        ? 'bg-primary text-primary-foreground font-bold shadow-xs'
                        : 'bg-card border border-border text-foreground-secondary hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Search Input */}
            <div className="relative min-w-[240px]">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                placeholder="Search PO #, supplier, plant, item..."
                className="w-full h-9 pl-9 pr-3 text-xs font-medium rounded-xl border border-input bg-background text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary font-poppins"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-4 overflow-x-auto">
          <DataTable
            data={records}
            columns={columns}
            isLoading={isLoading}
            pageSize={25}
            totalCount={total}
            searchPlaceholder="Search deliveries..."
            fixedHeight={true}
            containerHeightClass="max-h-[580px] overflow-y-auto overflow-x-auto custom-scrollbar"
            emptyTitle="No Delivery Records Found"
            emptyDescription="There are no active purchase orders matching your delivery filter criteria."
            onRowClick={(row) => navigate(ROUTES.PURCHASE_ORDERS.DETAILS(row.poId))}
          />
        </CardContent>
      </Card>

      {/* Expedite Notice Confirmation Modal */}
      {expediteTarget &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 animate-in fade-in"
            onClick={() => !isSendingExpedite && setExpediteTarget(null)}
          >
            <div
              className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl space-y-4 font-poppins animate-in zoom-in-95 my-auto text-foreground"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-border/60 pb-3">
                <div className="flex items-center gap-2 text-amber-600">
                  <BellRing className="h-5 w-5" />
                  <h3 className="text-base font-bold text-foreground font-poppins">
                    Issue Supplier Expedite Notice
                  </h3>
                </div>
                <button
                  onClick={() => !isSendingExpedite && setExpediteTarget(null)}
                  className="rounded-lg p-1 hover:bg-muted text-foreground-muted cursor-pointer"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Order Details Box */}
              <div className="rounded-xl bg-muted/40 border border-border p-3.5 space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    PO Number
                  </span>
                  <span className="font-mono font-bold text-primary">
                    {expediteTarget.poNumber}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    Supplier
                  </span>
                  <span className="font-bold text-foreground truncate max-w-[200px] text-right">
                    {expediteTarget.vendorName}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    Delay Status
                  </span>
                  <span className="font-bold text-rose-600">
                    {expediteTarget.delayDays} days past promised delivery
                  </span>
                </div>
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5 text-xs">
                  Expedite Notification Directive:
                </label>
                <textarea
                  rows={3}
                  value={expediteReason}
                  onChange={(e) => setExpediteReason(e.target.value)}
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-medium text-foreground focus:ring-2 focus:ring-primary font-poppins"
                />
              </div>

              {expediteSuccess && (
                <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 font-bold text-xs flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" /> Expedite notice transmitted successfully to {expediteTarget.vendorName}!
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-border/60">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isSendingExpedite}
                  onClick={() => setExpediteTarget(null)}
                  className="cursor-pointer font-bold rounded-xl font-poppins"
                >
                  Cancel
                </Button>
                <button
                  type="button"
                  disabled={isSendingExpedite}
                  onClick={handleSendExpediteNotice}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-600 hover:bg-amber-700 active:scale-95 text-white font-extrabold text-xs shadow-lg shadow-amber-600/30 transition-all cursor-pointer disabled:opacity-50 font-poppins"
                >
                  {isSendingExpedite ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>Transmitting...</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-3.5 w-3.5" />
                      <span>Transmit Notice</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Right Slide-Over Curtain Drawer for Delivery PO Dossier */}
      <SlideOverDrawer
        isOpen={Boolean(selectedPoForDrawer)}
        onClose={() => setSelectedPoForDrawer(null)}
        title="Delivery Purchase Order Dossier"
        subtitle="Tracking shipment line items, promised dates, and warehouse receipts"
        fullPageUrl={selectedPoForDrawer ? ROUTES.PURCHASE_ORDERS.DETAILS(selectedPoForDrawer) : undefined}
      >
        {selectedPoForDrawer && (
          <PurchaseOrderDetailPage
            recordId={selectedPoForDrawer}
            isDrawer={true}
            onClose={() => setSelectedPoForDrawer(null)}
          />
        )}
      </SlideOverDrawer>
    </div>
  );
};
